import { DOMParser, XMLSerializer } from 'xmldom';
import * as xpath from 'xpath';
import { ZoneType } from '../db/ZoneTypesDatatype';

type PolygonGroup = {
  type: XAG_PLACEMARK_TYPE;
  description: string;
  placemarks: Element[]; // Preserve full placemark to retain styles
};

export type XAG_PLACEMARK_TYPE = 'boundary' | 'nospray' | 'obstacle';
type XAG_PLACEMARK_STYLE_TYPE = `${XAG_PLACEMARK_TYPE}-style`;
export const VALID_XAG_PLACEMARK_TYPES: XAG_PLACEMARK_TYPE[] = ['boundary', 'nospray', 'obstacle'];

export const XAG_PLACEMARK_TYPE_TO_ZONE_TYPE: Record<XAG_PLACEMARK_TYPE, ZoneType> = {
  boundary: ZoneType.FIELD,
  nospray: ZoneType.SLOW,
  obstacle: ZoneType.UNSAFE,
};

const TYPE_MAPPING: Record<string, { type: XAG_PLACEMARK_TYPE; description: string }> = {
  field: { type: 'boundary', description: 'Boundaries' },
  slow: { type: 'nospray', description: 'No-Spray Areas' },
  unsafe: { type: 'obstacle', description: 'Obstacles' },
};

class KmlToXagConverter {
  private outputXag: Document;
  private select: xpath.XPathSelect;

  constructor(private inputKmls: { fieldName: string; kmlData: string }[]) {}

  convert() {
    if (this.inputKmls.length === 0) {
      throw new Error('No input files provided');
    }

    this.select = xpath.useNamespaces({ kml: 'http://www.opengis.net/kml/2.2' });

    // Output document
    this.outputXag = new DOMParser().parseFromString(
      `<?xml version="1.0" encoding="UTF-8"?>
     <kml xmlns="http://www.opengis.net/kml/2.2"><Document></Document></kml>`,
      'text/xml',
    );
    const documentNode = this.outputXag.getElementsByTagName('Document')[0];

    // Add Schema
    documentNode.appendChild(this.createSchema());

    this.inputKmls.forEach((inputKml) => {
      const { fieldName, kmlData } = inputKml;
      const inputDoc = new DOMParser().parseFromString(kmlData, 'text/xml');

      this.parseSingleKml(fieldName, inputDoc);
    });

    // Styling
    const boundaryStyle = this.createStyle('boundary-style');
    const nosprayStyle = this.createStyle('nospray-style');
    const obstacleStyle = this.createStyle('obstacle-style');

    documentNode.appendChild(boundaryStyle);
    documentNode.appendChild(nosprayStyle);
    documentNode.appendChild(obstacleStyle);

    return new XMLSerializer().serializeToString(this.outputXag);
  }

  private createSchema() {
    const schema = this.outputXag.createElement('Schema');
    schema.setAttribute('id', 'layer');
    schema.setAttribute('name', 'layer');

    const field1 = this.outputXag.createElement('SimpleField');
    field1.setAttribute('name', 'type');
    field1.setAttribute('type', 'string');

    const field2 = this.outputXag.createElement('SimpleField');
    field2.setAttribute('name', 'visualType');
    field2.setAttribute('type', 'string');
    schema.appendChild(field1);
    schema.appendChild(field2);

    return schema;
  }

  private parseSingleKml(fieldName: string, inputDoc: Document) {
    const documentNode = this.outputXag.getElementsByTagName('Document')[0];

    // Find the boundaries folder
    const folderNodes = this.select(
      '//kml:Folder[kml:name="target"]/kml:Folder[kml:name="boundaries"]',
      inputDoc,
    ) as Node[];

    const boundariesFolder =
      folderNodes.length > 0 && folderNodes[0].nodeType === 1 ? (folderNodes[0] as Element) : null;

    if (!boundariesFolder) {
      throw new Error('Boundaries folder not found in KML');
    }

    // Group placemarks by name -> type
    const placemarks = this.select('kml:Placemark', boundariesFolder) as Element[];
    const groups: Record<string, PolygonGroup> = {};

    for (const pm of placemarks) {
      const name = this.select('string(kml:name)', pm) as string;

      if (!(name in TYPE_MAPPING)) {
        continue;
      }

      if (!groups[name]) {
        groups[name] = {
          ...TYPE_MAPPING[name],
          placemarks: [],
        };
      }

      groups[name].placemarks.push(pm);
    }

    const folder = this.outputXag.createElement('Folder');
    const folderNameEl = this.outputXag.createElement('name');
    let finalFieldName = fieldName;

    // If not supplied as a parameter, use input kml to determine the field name.
    if (!finalFieldName) {
      const fieldNameNodes = this.select('//kml:Document/kml:name', inputDoc) as Node[];
      finalFieldName = fieldNameNodes.length > 0 ? (fieldNameNodes[0].textContent ?? 'Field') : 'Field';
    }

    folderNameEl.textContent = finalFieldName;
    folder.appendChild(folderNameEl);

    for (const group of Object.values(groups)) {
      const mergedPlacemark = this.outputXag.createElement('Placemark');

      // Metadata
      const nameEl = this.outputXag.createElement('name');
      nameEl.textContent = fieldName;
      mergedPlacemark.appendChild(nameEl);

      const descEl = this.outputXag.createElement('description');
      descEl.textContent = group.description;
      mergedPlacemark.appendChild(descEl);

      // Styling
      const styleUrl = this.outputXag.createElement('styleUrl');
      styleUrl.textContent = `${group.type}-style`;
      mergedPlacemark.appendChild(styleUrl);

      // ExtendedData
      const extendedData = this.outputXag.createElement('ExtendedData');
      const schemaData = this.outputXag.createElement('SchemaData');
      schemaData.setAttribute('schemaUrl', '#layer');

      const sdType = this.outputXag.createElement('SimpleData');
      sdType.setAttribute('name', 'type');
      sdType.textContent = group.type;

      const sdVisual = this.outputXag.createElement('SimpleData');
      sdVisual.setAttribute('name', 'visualType');
      sdVisual.textContent = group.type === 'obstacle' ? 'OBSTACLE' : 'BOUNDARY';

      schemaData.appendChild(sdType);
      schemaData.appendChild(sdVisual);
      extendedData.appendChild(schemaData);
      mergedPlacemark.appendChild(extendedData);

      // Collect all polygons (with holes)
      this.addAllPolygonsAsCollectionOfPolygons(group, folder, mergedPlacemark);
    }

    documentNode.appendChild(folder);
  }

  private addAllPolygonsAsCollectionOfPolygons(group: PolygonGroup, folder: HTMLElement, mergedPlacemark: HTMLElement) {
    if (group.placemarks.length === 1) {
      const polyNodes = this.select('.//kml:Polygon', group.placemarks[0]) as Node[];
      const poly = polyNodes.length > 0 && polyNodes[0].nodeType === 1 ? (polyNodes[0] as Element) : null;

      if (poly) {
        mergedPlacemark.appendChild(this.outputXag.importNode(poly, true));
      }
    } else {
      const multi = this.outputXag.createElement('MultiGeometry');

      for (const pm of group.placemarks) {
        const polyNodes = this.select('.//kml:Polygon', pm) as Node[];
        const poly = polyNodes.length > 0 && polyNodes[0].nodeType === 1 ? (polyNodes[0] as Element) : null;

        if (poly) {
          multi.appendChild(this.outputXag.importNode(poly, true));
        }
      }

      mergedPlacemark.appendChild(multi);
    }

    folder.appendChild(mergedPlacemark);
  }

  // This one fails when importing on XAG portal. Apparently, it needs a collection.
  private addAllPolygonsAsSimplePolygons(group: PolygonGroup, folder: HTMLElement, fieldName: string) {
    for (const pm of group.placemarks) {
      const polygonNodes = this.select('.//kml:Polygon', pm) as Element[];

      for (const polygon of polygonNodes) {
        const placemark = this.outputXag.createElement('Placemark');

        // Name
        const nameEl = this.outputXag.createElement('name');
        nameEl.textContent = fieldName;
        placemark.appendChild(nameEl);

        // Description
        const descEl = this.outputXag.createElement('description');
        descEl.textContent = group.description;
        placemark.appendChild(descEl);

        // Style
        const styleUrl = this.outputXag.createElement('styleUrl');
        styleUrl.textContent = `#${group.type}-style`;
        placemark.appendChild(styleUrl);

        // ExtendedData
        const extendedData = this.outputXag.createElement('ExtendedData');
        const schemaData = this.outputXag.createElement('SchemaData');
        schemaData.setAttribute('schemaUrl', '#layer');

        const sdType = this.outputXag.createElement('SimpleData');
        sdType.setAttribute('name', 'type');
        sdType.textContent = group.type;

        const sdVisual = this.outputXag.createElement('SimpleData');
        sdVisual.setAttribute('name', 'visualType');
        sdVisual.textContent = group.type === 'obstacle' ? 'OBSTACLE' : 'BOUNDARY';

        schemaData.appendChild(sdType);
        schemaData.appendChild(sdVisual);
        extendedData.appendChild(schemaData);
        placemark.appendChild(extendedData);

        // Polygon
        placemark.appendChild(this.outputXag.importNode(polygon, true));

        // Add to folder
        folder.appendChild(placemark);
      }
    }
  }

  private createStyle(id: XAG_PLACEMARK_STYLE_TYPE): Element {
    const style = this.outputXag.createElement('Style');
    style.setAttribute('id', id);

    const lineStyle = this.outputXag.createElement('LineStyle');
    const lineColor = this.outputXag.createElement('color');
    const lineWidth = this.outputXag.createElement('width');

    const polyStyle = this.outputXag.createElement('PolyStyle');
    const fill = this.outputXag.createElement('fill');
    const fillColor = this.outputXag.createElement('color');

    if (id === 'boundary-style') {
      lineColor.textContent = 'ffff0000';
      lineWidth.textContent = '2';
      fill.textContent = '1';
      fillColor.textContent = '00ff0000';
    } else if (id === 'obstacle-style') {
      lineColor.textContent = 'ffffffff';
      lineWidth.textContent = '1';
      fill.textContent = '1';
      fillColor.textContent = '4d0000ff';
    } else if (id === 'nospray-style') {
      lineColor.textContent = 'ffffffff';
      lineWidth.textContent = '1';
      fill.textContent = '1';
      fillColor.textContent = '4d00ffff';
    }

    lineStyle.appendChild(lineColor);
    lineStyle.appendChild(lineWidth);
    polyStyle.appendChild(fill);
    polyStyle.appendChild(fillColor);

    style.appendChild(lineStyle);
    style.appendChild(polyStyle);

    return style;
  }
}

export default KmlToXagConverter;
